import { Field, useFormikContext } from 'formik';
import { useEffect, useLayoutEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import camelCase from '../../../utils/camelCase';
import { DeliveryOption, LOADING_TYPES, useCheckoutState } from '../../../utils/checkoutState';
import { config, getAssetUrl } from '../../../utils/config';
import { pushErrorTrackingEventFromErrorObject } from '../../../utils/tracking';
import useDeepCompare from '../../../utils/useDeepCompare';
import Button from '../../atoms/Button';
import Collapse from '../../atoms/Collapse';
import Paragraph from '../../atoms/Paragraph';
import Price from '../../atoms/Price';
import SubHeadline from '../../atoms/SubHeadline';
import OptionsField from '../../formik/OptionsField';
import { showDefaultError } from '../Notifications';
import ScheduledDeliveryForm from './ScheduledDeliveryForm';
import ShippingOverview from './ShippingOverview';
import './style.scss';

type AdditionalCarrierConfig = {
  i18nKey?: string;
};

const additionalConfig: Record<string, AdditionalCarrierConfig> = {
  at_dhl_4950: { i18nKey: 'dhl' },
  at_hermes_2900: { i18nKey: 'hermes' },
  at_postat_2900: { i18nKey: 'postat' },
  at_postat_free: { i18nKey: 'atPostAtFree' },
  at_postat_freedelivery: { i18nKey: 'atPostAtFree' },
  at_postat_standard: { i18nKey: 'atPostAtStandard' },
  be_gls_freedelivery: { i18nKey: 'glsFreeDelivery' },
  be_gls_standard: { i18nKey: 'gls' },
  bpost_standard: { i18nKey: 'bpostbeFree' },
  bpostbe_free: { i18nKey: 'bpostbeRegular' },
  bpostbe: { i18nKey: 'bpost' },
  brt_free: { i18nKey: 'bartoliniFree' },
  brt_standard: { i18nKey: 'bartolini' },
  brt: { i18nKey: 'bartolini' },
  ch_hermes_5995: { i18nKey: 'hermes' },
  ch_post_freedelivery: { i18nKey: 'postchFreedelivery' },
  ch_post_standard: { i18nKey: 'postch' },
  ch_swiss_post_1295: { i18nKey: 'postch' },
  chronopost_free: { i18nKey: 'chronopostFree' },
  chronopost_standard: { i18nKey: 'chronopost' },
  colissimo_sfr_free: { i18nKey: 'colissimoSfrFree' },
  colissimo_sfr_regular: { i18nKey: 'colissimoSfrRegular' },
  colissimo_sfr: { i18nKey: 'colissimo' },
  de_dhl_4950: { i18nKey: 'dhl' },
  de_dhl_freedelivery: { i18nKey: 'freeDeliveryDhl' },
  de_hermes_2900: { i18nKey: 'hermes' },
  DHL_EXPRESS: { i18nKey: 'dhl' },
  dhl_express: { i18nKey: 'expressDelivery' },
  DHL_FR: { i18nKey: 'dhl' },
  dhl_bde: { i18nKey: 'dhl' },
  dhlint_bde: { i18nKey: 'dhlInternational' },
  dhlint_fde: { i18nKey: 'dhlInternational' },
  dhlint_sde: { i18nKey: 'dhlInternational' },
  dhl_free: { i18nKey: 'freeDeliveryDhl' },
  dhl_fde: { i18nKey: 'dhl' },
  dhl_pl_free: { i18nKey: 'dhlPlFree' },
  dhl_pl_regular: { i18nKey: 'dhlPlRegular' },
  dhl_pl: { i18nKey: 'dhl' },
  dhl_sde_free: { i18nKey: 'dhlSdeFree' },
  dhl_sde_regular: { i18nKey: 'dhlSdeRegular' },
  dhl_sde: { i18nKey: 'dhl' },
  dhl_spl_free: { i18nKey: 'dhlSplFree' },
  dhl_spl_regular: { i18nKey: 'dhlSplRegular' },
  dhl_spl: { i18nKey: 'dhl' },
  dhl_standard: { i18nKey: 'standardDelivery' },
  dhlde_sfr_free: { i18nKey: 'dhldeSfrFree' },
  dhlde_sfr_regular: { i18nKey: 'dhldeSfrRegular' },
  dhlde_sfr: { i18nKey: 'dhl' },
  dhlse: { i18nKey: 'dhl' },
  dhl_nat: { i18nKey: 'dhl' },
  dhl_eur: { i18nKey: 'dhl' },
  dpd_lt_free: { i18nKey: 'dpdLtFree' },
  dpd_lt_regular: { i18nKey: 'dpdLtRegular' },
  dpd_lt: { i18nKey: 'dpd' },
  eu_ups_freedelivery: { i18nKey: 'upsFreeDelivery' },
  eu_ups_standard: { i18nKey: 'ups' },
  evri_gb: { i18nKey: 'evri' },
  evri_express_gb: { i18nKey: 'evriExpress' },
  express_delivery_dhl: { i18nKey: 'expressDelivery' },
  FAN_RO: { i18nKey: 'fanro' },
  fedex_ses_free: { i18nKey: 'fedexSesFree' },
  fedex_ses_regular: { i18nKey: 'fedexSesRegular' },
  fedex_ses: { i18nKey: 'fedex' },
  GLS_DK: { i18nKey: 'gls' },
  glsdk: { i18nKey: 'gls' },
  glshu: { i18nKey: 'gls' },
  gls_hu: { i18nKey: 'gls' },
  hermes_post_at_free: { i18nKey: 'postAtFree' },
  hermes_sde_free: { i18nKey: 'hermesSdeFree' },
  hermes_sde_regular: { i18nKey: 'hermesSdeRegular' },
  hermes_sde: { i18nKey: 'hermes' },
  hermes_bde: { i18nKey: 'hermes' },
  hermes_fde: { i18nKey: 'hermes' },
  hermes_standard: { i18nKey: 'deHermesStandard' },
  inpost_pl_free: { i18nKey: 'inpostPlFree' },
  inpost_pl_regular: { i18nKey: 'inpostPlRegular' },
  inpost_pl: { i18nKey: 'inpost' },
  inpost_spl_free: { i18nKey: 'inpostSplFree' },
  inpost_spl_regular: { i18nKey: 'inpostSplRegular' },
  inpost_spl: { i18nKey: 'inpost' },
  la_poste_regular: { i18nKey: 'laposte' },
  la_poste: { i18nKey: 'laposte' },
  laposte_standard: { i18nKey: 'laposte' },
  mpl_hu: { i18nKey: 'posthu' },
  nl_postnl_freedelivery: { i18nKey: 'postnlFreedelivery' },
  nl_postnl_standard: { i18nKey: 'postnl' },
  post_at_free: { i18nKey: 'postAtFree' },
  post_at_regular: { i18nKey: 'postAtRegular' },
  post_at: { i18nKey: 'postat' },
  post_bde: { i18nKey: 'postat' },
  post_ch_free: { i18nKey: 'postchFreedelivery' },
  postat_fde: { i18nKey: 'postat' },
  postat_free: { i18nKey: 'postAtFree' },
  postat_sat_free: { i18nKey: 'postatSatFree' },
  postat_sat_regular: { i18nKey: 'postatSatRegular' },
  postat_sat: { i18nKey: 'postat' },
  postat_standard_delivery: { i18nKey: 'atPostAtStandard' },
  postat_standard: { i18nKey: 'postAtRegular' },
  postch_dos: { i18nKey: 'postch' },
  postch_free: { i18nKey: 'postchFreedelivery' },
  postch_och: { i18nKey: 'postchFreedelivery' },
  postch_sch_free: { i18nKey: 'postchSchFree' },
  postch_sch_regular: { i18nKey: 'postchSchRegular' },
  postch_sch: { i18nKey: 'postch' },
  posteitaliane_new: { i18nKey: 'posteitaliane' },
  postnl_bde: { i18nKey: 'postnl' },
  postnl_fde: { i18nKey: 'postnl' },
  postnl_free: { i18nKey: 'postnlFreedelivery' },
  postnl_sbe_free: { i18nKey: 'postnlSbeFree' },
  postnl_sbe_regular: { i18nKey: 'postnlSbeRegular' },
  postnl_sbe: { i18nKey: 'postnl' },
  postnl_snl_free: { i18nKey: 'postnlSnlFree' },
  postnl_snl_regular: { i18nKey: 'postnlSnlRegular' },
  postnl_snl: { i18nKey: 'postnl' },
  postnl_standard_delivery: { i18nKey: 'postnlStandard' },
  PostNord_SE: { i18nKey: 'postnord' },
  sda_sit_free: { i18nKey: 'sdaSitFree' },
  sda_sit_regular: { i18nKey: 'sdaSitRegular' },
  sda_sit: { i18nKey: 'sda' },
  seur_es: { i18nKey: 'seur' },
  SEUR_ES: { i18nKey: 'seur' },
  SEUR_PT: { i18nKey: 'seur' },
  seur_ses_free: { i18nKey: 'seurSesFree' },
  seur_ses_regular: { i18nKey: 'seurSesRegular' },
  seur_ses: { i18nKey: 'seur' },
  standard_delivery_495: { i18nKey: 'standardDelivery' },
  swisspost: { i18nKey: 'postch' },
  UB_POST_PL: { i18nKey: 'inpost' },
  ups_bde: { i18nKey: 'ups' },
  ups_fde: { i18nKey: 'ups' },
  ups_sde: { i18nKey: 'ups' },
  upsexpress_bde: { i18nKey: 'upsExpress' },
  upsexpress_fde: { i18nKey: 'upsExpress' },
  ups_ses_free: { i18nKey: 'upsSesFree' },
  ups_ses_regular: { i18nKey: 'upsSesRegular' },
  ups_ses: { i18nKey: 'ups' },
  ups_sit_free: { i18nKey: 'upsSitFree' },
  ups_sit_regular: { i18nKey: 'upsSitRegular' },
  ups_sit: { i18nKey: 'ups' },
  ups_spt_free: { i18nKey: 'upsSptFree' },
  ups_spt_regular: { i18nKey: 'upsSptRegular' },
  ups_spt: { i18nKey: 'ups' },
  ups_eur: { i18nKey: 'ups' },
  ups_us: { i18nKey: 'ups' },
  yurtici_tr_free: { i18nKey: 'yurticiTrFree' },
  yurtici_tr_regular: { i18nKey: 'yurticiTrRegular' },
  yurtici_tr: { i18nKey: 'yurtici' }
};

export const getCarrierI18nKey = (key: string) => additionalConfig[key]?.i18nKey ?? camelCase(key);

const groupCarriers = (
  carriers: (Pick<DeliveryOption, 'carrierKey' | 'carrierGroupKey'> & {
    shippingOptions: DeliveryOption[];
  })[],
  deliveryOption: DeliveryOption
) => {
  const carrier = carriers.find(({ carrierGroupKey }) => carrierGroupKey === deliveryOption.carrierGroupKey);
  if (carrier) {
    carrier.shippingOptions.push(deliveryOption);
  } else {
    carriers.push({
      carrierKey: deliveryOption.carrierKey,
      carrierGroupKey: deliveryOption.carrierGroupKey,
      shippingOptions: [deliveryOption]
    });
  }
  return carriers;
};

type CarrierOptionsProps = {
  isEditingCarriers?: boolean;
  setEditingCarriers?: (value: boolean) => void;
  navigateToCp?: () => void;
};

const CarrierOptions = ({ isEditingCarriers, setEditingCarriers, navigateToCp }: CarrierOptionsProps) => {
  const { checkoutState, setCarrierAndShippingOption, isLoading, setLoading, filledSlots } = useCheckoutState();
  const selectedDelivery = checkoutState.orderState.shipping?.selected?.[0];
  const isDeliveryOptionsSlotFilled = filledSlots.deliveryOptions;
  const isCustomDeliveryOption = config.features.ingrid.enabled || isDeliveryOptionsSlotFilled;

  const { formatMessage } = useIntl();
  const {
    values: { carrier },
    setFieldValue,
    setFieldTouched
  } = useFormikContext<{ carrier: Pick<DeliveryOption, 'carrierKey' | 'carrierGroupKey' | 'shippingOptionKey'> }>();

  const carriers =
    checkoutState.orderOptions?.shipping?.homeDelivery
      ?.filter(({ isDisabled }) => !isDisabled)
      .reduce(groupCarriers, []) ?? [];

  const carrierShippingOptions =
    (carriers.find(({ carrierGroupKey }) => carrierGroupKey === carrier.carrierGroupKey) ?? carriers[0])
      ?.shippingOptions ?? [];

  const currentShipping = useDeepCompare(checkoutState.orderState.shipping?.selected?.[0]);

  const isShortDescriptionEnabled = config.shippingOptions.descriptions.short;
  const isToolTipEnabled = config.shippingOptions.descriptions.toolTip;
  const { showNetPrices } = config.basket;

  const { suggestCollectionPoint, isCollapsible, position } = config.carrierOptions;

  const suggestCp = position === 'payment' && suggestCollectionPoint.enabled;

  const isOnlyOneShippingOptionAvailable = carriers.length === 1 && carrierShippingOptions.length === 1 && !suggestCp;

  useLayoutEffect(() => {
    if (selectedDelivery && isOnlyOneShippingOptionAvailable) {
      setEditingCarriers?.(false);
    }
  }, []);

  useEffect(() => {
    if (currentShipping && carrier.carrierGroupKey !== currentShipping.carrierGroupKey) {
      setFieldValue('carrier.carrierKey', currentShipping.carrierKey);
      setFieldValue('carrier.carrierGroupKey', currentShipping.carrierGroupKey);
      setFieldValue('carrier.shippingOptionKey', currentShipping.shippingOptionKey);
    }
  }, [currentShipping]);

  if (isCustomDeliveryOption) {
    return null;
  }

  if (!carriers.length) {
    return (
      <>
        <SubHeadline>
          <FormattedMessage id="shipping.carrier.headline" />
        </SubHeadline>
        <Paragraph>
          <FormattedMessage id="shipping.notifications.noDeliveryMethods" />
        </Paragraph>
      </>
    );
  }

  const carrierOptions =
    carriers.length > 0 ?
      carriers.map(({ carrierGroupKey }) => {
        const { i18nKey } = additionalConfig[carrierGroupKey] ?? { i18nKey: camelCase(carrierGroupKey) };
        return {
          key: carrierGroupKey,
          label: formatMessage({ id: `shipping.carrier.options.${i18nKey}` }),
          defaultIcon: getAssetUrl(`carriers/${i18nKey}.svg`)
        };
      })
    : undefined;

  const getShortDescription = (shippingOptionKey: string) => {
    if (!isShortDescriptionEnabled) {
      return undefined;
    }
    return formatMessage({ id: `shipping.carrier.optionDescriptions.${shippingOptionKey}.shortDescription` });
  };

  const getToolTip = (shippingOptionKey: string) => {
    const headline = formatMessage({ id: `shipping.carrier.optionDescriptions.${shippingOptionKey}.toolTipHeadline` });
    const content = formatMessage({ id: `shipping.carrier.optionDescriptions.${shippingOptionKey}.toolTipText` });
    return { headline, content };
  };

  const hasScheduledDeliveryOption = carrierShippingOptions.find(
    carrierShippingOption => carrierShippingOption.shippingOptionKey === 'scheduled_delivery'
  );

  const shippingOptions =
    carrierShippingOptions.length > 1 || hasScheduledDeliveryOption || suggestCp ?
      carrierShippingOptions.map(({ shippingOptionKey, serviceCosts, deliveryEstimations }) => {
        const i18nKey = getCarrierI18nKey(shippingOptionKey);
        const shippingServiceCosts = showNetPrices ? serviceCosts?.withoutTax : serviceCosts?.withTax;

        return {
          key: shippingOptionKey,
          label: `${formatMessage({ id: `shipping.carrier.options.${i18nKey}` })}${
            deliveryEstimations?.deliveryDays && !config.shippingOptions.hideDeliveryEstimation ?
              ` (${formatMessage(
                { id: 'shipping.carrier.deliveryEstimate' },
                { min: deliveryEstimations.deliveryDays.minimum, max: deliveryEstimations.deliveryDays.maximum }
              )})`
            : ''
          }`,
          labelRight:
            shippingServiceCosts !== undefined ?
              shippingServiceCosts !== 0 ?
                <Price price={shippingServiceCosts} isLarge />
              : <FormattedMessage id="generic.free" />
            : null,
          shortDescription: getShortDescription(i18nKey!),
          ...(isToolTipEnabled && { toolTip: getToolTip(i18nKey!) }),
          ...(shippingOptionKey === 'scheduled_delivery' && { form: <ScheduledDeliveryForm /> })
        };
      })
    : undefined;

  const onChange = async (payload: {
    data: Pick<DeliveryOption, 'carrierKey' | 'carrierGroupKey' | 'shippingOptionKey' | 'deliveryOptionKey'>;
    specification?: { deliveryDate: string };
  }) => {
    setLoading(LOADING_TYPES.carrierChange(`${payload.data.carrierGroupKey}-${payload.data.shippingOptionKey}`));
    try {
      await setCarrierAndShippingOption(payload);
    } catch (e) {
      console.error(e);
      showDefaultError();
      pushErrorTrackingEventFromErrorObject(e, 'set_carrier_and_shipping_option');
    }
    setLoading(LOADING_TYPES.off);
  };

  const onCarrierOptionChange = (carrierGroupKey: string) => {
    const newCarrier = carriers.find(eachCarrier => eachCarrier.carrierGroupKey === carrierGroupKey)!;
    setFieldValue('carrier.carrierKey', newCarrier.carrierKey);
    const firstShippingOption = newCarrier.shippingOptions[0];
    setFieldValue('carrier.shippingOptionKey', firstShippingOption.shippingOptionKey);
    onShippingOptionChange(firstShippingOption.shippingOptionKey, newCarrier.carrierKey, newCarrier.carrierGroupKey);
  };

  const onShippingOptionChange = (
    shippingOptionKey: string,
    firstCarrierKey?: string,
    firstCarrierGroupKey?: string
  ) => {
    if (shippingOptionKey === 'collection_point_suggestion') {
      navigateToCp?.();
      return;
    }
    const data = {
      carrierKey: firstCarrierKey ?? carrier.carrierKey,
      carrierGroupKey: firstCarrierGroupKey ?? carrier.carrierGroupKey,
      shippingOptionKey,
      deliveryOptionKey: 'home_delivery'
    };

    if (shippingOptionKey === 'scheduled_delivery') {
      const minDeliveryDate = carrierShippingOptions.find(
        shippingOption => shippingOption.shippingOptionKey === 'scheduled_delivery'
      )?.deliveryDates?.minimum;

      onChange({
        data,
        specification: {
          deliveryDate: minDeliveryDate!
        }
      });

      setFieldValue('carrier.deliveryDate', '');
      setFieldTouched('carrier.deliveryDate', false);
    } else {
      onChange({ data });
    }
  };

  const getLoadingString = (place: 'carrierKey' | 'shippingKey') => {
    if (isLoading.includes('carrier:change-')) {
      const splittedLoading = isLoading.replace('carrier:change-', '').split('-');
      return place === 'carrierKey' ? splittedLoading[0] : splittedLoading[1];
    } else {
      return isLoading;
    }
  };

  const collectionPointOption = {
    key: 'collection_point_suggestion',
    label: formatMessage({ id: 'address.selectCollectionPoint' }),
    labelRight: null,
    defaultIcon: getAssetUrl('delivery/collectionPointActive.svg'),
    shortDescription: getShortDescription('collectionPoint'),
    ...(isToolTipEnabled && { toolTip: getToolTip('collectionPoint') })
  };

  if (suggestCp) {
    shippingOptions?.push(collectionPointOption);
  }

  const carrierOptionsComponent = (
    <>
      {!isCollapsible.enabled && (
        <SubHeadline>
          <FormattedMessage id="shipping.carrier.headline" />
        </SubHeadline>
      )}
      {carrierOptions && (
        <Field
          name="carrier.carrierGroupKey"
          className="carrier__options"
          component={OptionsField}
          options={carrierOptions}
          dataTestIdPrefix="carrierOptions"
          isLoading={getLoadingString('carrierKey')}
          onChange={onCarrierOptionChange}
        />
      )}
      {shippingOptions && (
        <div className="carrier-options__carrier-delivery-type-options">
          <Field
            name="carrier.shippingOptionKey"
            component={OptionsField}
            options={shippingOptions}
            dataTestIdPrefix="carrierShippingOptions"
            isVertical
            isLoading={getLoadingString('shippingKey')}
            onChange={onShippingOptionChange}
          />
        </div>
      )}
    </>
  );

  if (selectedDelivery && isOnlyOneShippingOptionAvailable && !hasScheduledDeliveryOption) {
    return null;
  }

  if (!isCollapsible.enabled) {
    return <>{carrierOptionsComponent}</>;
  } else {
    return (
      <div className="carrier-overview" data-test-id="carrier-options">
        {!isEditingCarriers && (
          <Button
            className="carrier-overview__btn"
            disabled={!!isLoading}
            onClick={() => {
              setEditingCarriers?.(true);
            }}
            data-test-id="carrier-overview_change-btn"
          >
            <FormattedMessage id="generic.change" />
          </Button>
        )}
        <dt className="carrier-overview__title">
          <FormattedMessage id={'shipping.carrier.headline'} />
        </dt>
        <Collapse isOpen={!isEditingCarriers} key="carrier-overview" dep={selectedDelivery}>
          <ShippingOverview />
        </Collapse>
        <Collapse isOpen={!!isEditingCarriers} key="carrier-selection" dep={carrier}>
          <>
            {carrierOptionsComponent}
            <Button
              type="button"
              className="carrier-overview__apply-btn"
              onClick={() => {
                setEditingCarriers?.(false);
              }}
              disabled={isLoading.includes('carrier:change') || !selectedDelivery}
              data-test-id="carrier-overview__apply-btn"
            >
              <FormattedMessage id="generic.apply" />
            </Button>
          </>
        </Collapse>
      </div>
    );
  }
};

export default CarrierOptions;
